import React, { useState, useEffect, useCallback } from "react";
import {
  getApplications, createCategorieApplication, renameCategorieApplication, deleteCategorieApplication,
  ordonnerCategoriesApplications, ordonnerLiensApplications, createLienApplication, updateLienApplication,
  deleteLienApplication,
} from "../services/api";
import ModaleV2 from "../components/ModaleV2";
import IconeV2 from "../components/IconeV2";

// ===================================================================
// The Crowd v2 — Pilotage › Liens
//
// Gestion de la section Applications de l'accueil, reservee aux
// directeurs (le backend le verifie aussi, applications.py) :
// - categories : ajout, renommage, ordre, suppression si vide ;
// - liens : ajout, modification (y compris la categorie), ordre dans la
//   categorie, suppression definitive apres confirmation.
// Les changements sont visibles sur l'accueil au prochain chargement.
// ===================================================================

const LIEN_VIDE = { categorie_id: "", nom: "", description: "", url: "", reseau_local: false };

// Permute deux elements voisins et renvoie la liste des identifiants
function permuter(liste, index, sens) {
  const cible = index + sens;
  if (cible < 0 || cible >= liste.length) return null;
  const ids = liste.map((x) => x.id);
  [ids[index], ids[cible]] = [ids[cible], ids[index]];
  return ids;
}

// --- Modales ---

function ModaleCategorie({ categorie, onValider, onClose }) {
  const [titre, setTitre] = useState(categorie?.titre || "");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await onValider(titre.trim());
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  return (
    <ModaleV2
      titre={categorie ? "Renommer la catégorie" : "Nouvelle catégorie"}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={valider} disabled={enCours || !titre.trim()}>
            {enCours ? "Enregistrement…" : "Enregistrer"}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-liens-cat-titre">Titre</label>
        <input id="v2-liens-cat-titre" value={titre} maxLength={100} onChange={(e) => setTitre(e.target.value)} autoFocus />
      </div>
    </ModaleV2>
  );
}

function ModaleLien({ lien, categories, categorieParDefaut, onValider, onClose }) {
  const [valeurs, setValeurs] = useState(
    lien
      ? { categorie_id: String(lien.categorie_id), nom: lien.nom, description: lien.description || "", url: lien.url, reseau_local: lien.reseau_local }
      : { ...LIEN_VIDE, categorie_id: categorieParDefaut ? String(categorieParDefaut) : "" }
  );
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const changer = (champ) => (e) =>
    setValeurs((v) => ({ ...v, [champ]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await onValider({
        categorie_id: Number(valeurs.categorie_id),
        nom: valeurs.nom.trim(),
        description: valeurs.description.trim() || null,
        url: valeurs.url.trim(),
        reseau_local: valeurs.reseau_local,
      });
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  const complet = valeurs.categorie_id && valeurs.nom.trim() && valeurs.url.trim();

  return (
    <ModaleV2
      titre={lien ? "Modifier le lien" : "Nouveau lien"}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={valider} disabled={enCours || !complet}>
            {enCours ? "Enregistrement…" : "Enregistrer"}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-liens-categorie">Catégorie<span className="v2-requis">*</span></label>
        <select id="v2-liens-categorie" value={valeurs.categorie_id} onChange={changer("categorie_id")}>
          <option value="">—</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.titre}</option>)}
        </select>
      </div>
      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-liens-nom">Nom<span className="v2-requis">*</span></label>
        <input id="v2-liens-nom" value={valeurs.nom} maxLength={100} onChange={changer("nom")} autoFocus />
      </div>
      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-liens-description">Descriptif</label>
        <input id="v2-liens-description" value={valeurs.description} maxLength={300} onChange={changer("description")} />
      </div>
      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-liens-url">Adresse<span className="v2-requis">*</span></label>
        <input id="v2-liens-url" type="url" value={valeurs.url} maxLength={1000} placeholder="https://" onChange={changer("url")} />
        <span className="v2-champ-aide">Commence par http:// ou https://. Le lien s'ouvre dans un nouvel onglet.</span>
      </div>
      <label className="v2-case-simple">
        <input type="checkbox" checked={valeurs.reseau_local} onChange={changer("reseau_local")} />
        <span>Accessible uniquement depuis le réseau local</span>
      </label>
    </ModaleV2>
  );
}

function ModaleConfirmation({ titre, texte, libelle, onValider, onClose }) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await onValider();
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  return (
    <ModaleV2
      titre={titre}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={valider} disabled={enCours}>
            {enCours ? "Suppression…" : libelle}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
      <p className="v2-champ-aide">{texte}</p>
    </ModaleV2>
  );
}

// --- Bloc d'une categorie ---

function BlocCategorie({ categorie, index, total, onMonter, onDescendre, onRenommer, onSupprimer,
  onAjouterLien, onModifierLien, onSupprimerLien, onDeplacerLien }) {
  const vide = categorie.liens.length === 0;
  return (
    <section className="v2-panneau v2-liens-categorie" aria-labelledby={`v2-liens-cat-${categorie.id}`}>
      <div className="v2-liens-entete">
        <div className="v2-liens-ordre" role="group" aria-label="Ordre de la catégorie">
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={onMonter} disabled={index === 0} aria-label="Monter la catégorie">↑</button>
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={onDescendre} disabled={index === total - 1} aria-label="Descendre la catégorie">↓</button>
        </div>
        <h2 id={`v2-liens-cat-${categorie.id}`} className="v2-bloc-titre">
          {categorie.titre}<span className="v2-projet-compte">{categorie.liens.length}</span>
        </h2>
        <div className="v2-liens-actions">
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={onAjouterLien}>
            <IconeV2 nom="plus" /> Lien
          </button>
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={onRenommer}>
            <IconeV2 nom="crayon" /> Renommer
          </button>
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact v2-btn-danger-texte" onClick={onSupprimer}
            disabled={!vide} title={vide ? undefined : "Déplacez ou supprimez d'abord ses liens"}>
            Supprimer
          </button>
        </div>
      </div>

      {vide ? (
        <p className="v2-vide">Aucun lien dans cette catégorie.</p>
      ) : (
        <div className="v2-tableau-conteneur">
          <table className="v2-tableau v2-liens-tableau">
            <thead>
              <tr>
                <th scope="col" className="v2-liens-col-ordre">Ordre</th>
                <th scope="col">Nom</th>
                <th scope="col">Descriptif</th>
                <th scope="col">Adresse</th>
                <th scope="col" className="v2-liens-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categorie.liens.map((l, i) => (
                <tr key={l.id}>
                  <td>
                    <span className="v2-liens-ordre">
                      <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => onDeplacerLien(i, -1)} disabled={i === 0} aria-label={`Monter ${l.nom}`}>↑</button>
                      <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => onDeplacerLien(i, 1)} disabled={i === categorie.liens.length - 1} aria-label={`Descendre ${l.nom}`}>↓</button>
                    </span>
                  </td>
                  <td className="v2-gras">
                    {l.nom}
                    {l.reseau_local && <> <span className="v2-statut v2-statut-neutre">Réseau local</span></>}
                  </td>
                  <td className="v2-cellule-discrete">{l.description || "—"}</td>
                  <td className="v2-liens-url">
                    <a href={l.url} target="_blank" rel="noopener noreferrer" title={l.url}>{l.url}</a>
                  </td>
                  <td>
                    <span className="v2-liens-actions">
                      <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => onModifierLien(l)}>
                        <IconeV2 nom="crayon" /> Modifier
                      </button>
                      <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact v2-btn-danger-texte" onClick={() => onSupprimerLien(l)}>
                        Supprimer
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// --- Page ---

function GestionLiensV2({ collaborateur }) {
  const estDirecteur = collaborateur?.role === "directeur";
  const [categories, setCategories] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);
  const [modale, setModale] = useState(null); // { type, ... }

  const charger = useCallback(() => {
    setErreur(null);
    return getApplications()
      .then(setCategories)
      .catch((err) => setErreur(`Liens non chargés : ${err.message}`));
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!estDirecteur) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-msg v2-msg-erreur">La gestion des liens est réservée aux directeurs.</div>
      </div>
    );
  }

  const cid = collaborateur.id;
  const fermer = () => setModale(null);

  // Action suivie d'un message et d'un rechargement ; l'erreur remonte a la modale
  const executer = async (action, texte) => {
    await action();
    setModale(null);
    setMessage({ type: "succes", texte });
    await charger();
  };

  // Reordonnancement direct (sans modale) : l'erreur s'affiche en haut de page
  const reordonner = async (appel) => {
    setMessage(null);
    try {
      await appel();
      await charger();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Ordre non enregistré : ${err.message}` });
    }
  };

  const liste = categories || [];
  const nbLiens = liste.reduce((s, c) => s + c.liens.length, 0);

  return (
    <div className="v2-page v2-page-module">
      <div className="v2-bib-entete">
        <header className="v2-module-entete">
          <h1 className="v2-module-titre">Liens</h1>
          <p className="v2-sous-titre">
            La section Applications de l'accueil : {liste.length} catégorie{liste.length > 1 ? "s" : ""}, {nbLiens} lien{nbLiens > 1 ? "s" : ""}.
          </p>
        </header>
        <div className="v2-liens-actions">
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setModale({ type: "categorie" })}>
            <IconeV2 nom="plus" /> Nouvelle catégorie
          </button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={() => setModale({ type: "lien" })} disabled={liste.length === 0}>
            <IconeV2 nom="plus" /> Nouveau lien
          </button>
        </div>
      </div>

      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}
      {!categories && !erreur && <div className="v2-chargement">Chargement…</div>}
      {categories && liste.length === 0 && (
        <div className="v2-panneau"><p className="v2-vide">Aucune catégorie. Commencez par en créer une.</p></div>
      )}

      <div className="v2-liens-liste">
        {liste.map((c, i) => (
          <BlocCategorie
            key={c.id}
            categorie={c}
            index={i}
            total={liste.length}
            onMonter={() => reordonner(() => ordonnerCategoriesApplications(permuter(liste, i, -1), cid))}
            onDescendre={() => reordonner(() => ordonnerCategoriesApplications(permuter(liste, i, 1), cid))}
            onRenommer={() => setModale({ type: "categorie", categorie: c })}
            onSupprimer={() => setModale({ type: "supprimerCategorie", categorie: c })}
            onAjouterLien={() => setModale({ type: "lien", categorieParDefaut: c.id })}
            onModifierLien={(l) => setModale({ type: "lien", lien: l })}
            onSupprimerLien={(l) => setModale({ type: "supprimerLien", lien: l })}
            onDeplacerLien={(index, sens) => {
              const ids = permuter(c.liens, index, sens);
              if (ids) reordonner(() => ordonnerLiensApplications(c.id, ids, cid));
            }}
          />
        ))}
      </div>

      {modale?.type === "categorie" && (
        <ModaleCategorie
          categorie={modale.categorie}
          onClose={fermer}
          onValider={(titre) => executer(
            () => (modale.categorie ? renameCategorieApplication(modale.categorie.id, titre, cid) : createCategorieApplication(titre, cid)),
            modale.categorie ? `Catégorie renommée : « ${titre} ».` : `Catégorie « ${titre} » créée.`,
          )}
        />
      )}
      {modale?.type === "lien" && (
        <ModaleLien
          lien={modale.lien}
          categories={liste}
          categorieParDefaut={modale.categorieParDefaut}
          onClose={fermer}
          onValider={(data) => executer(
            () => (modale.lien ? updateLienApplication(modale.lien.id, data, cid) : createLienApplication(data, cid)),
            modale.lien ? `Lien « ${data.nom} » mis à jour.` : `Lien « ${data.nom} » ajouté.`,
          )}
        />
      )}
      {modale?.type === "supprimerCategorie" && (
        <ModaleConfirmation
          titre="Supprimer la catégorie"
          texte={`La catégorie « ${modale.categorie.titre} » sera supprimée. Elle ne contient aucun lien.`}
          libelle="Supprimer"
          onClose={fermer}
          onValider={() => executer(
            () => deleteCategorieApplication(modale.categorie.id, cid),
            `Catégorie « ${modale.categorie.titre} » supprimée.`,
          )}
        />
      )}
      {modale?.type === "supprimerLien" && (
        <ModaleConfirmation
          titre="Supprimer le lien"
          texte={`« ${modale.lien.nom} » sera supprimé définitivement de l'accueil. Cette action ne peut pas être annulée.`}
          libelle="Supprimer définitivement"
          onClose={fermer}
          onValider={() => executer(
            () => deleteLienApplication(modale.lien.id, cid),
            `Lien « ${modale.lien.nom} » supprimé.`,
          )}
        />
      )}
    </div>
  );
}

export default GestionLiensV2;
